import type {
  Completion,
  CompletionContext,
  CompletionResult,
} from "@codemirror/autocomplete"
import { syntaxTree } from "@codemirror/language"
import type { SyntaxNode } from "@lezer/common"
import type { EditorState } from "@codemirror/state"
import {
  COMMON_KEYWORDS,
  JSON_STRING_FORMATS,
  JsonFieldType,
  KEYWORD_DESCRIPTIONS,
  NULL,
  TYPE_KEYWORDS,
} from "./model"

/**
 * Completes a JsonField's property names and `type`/`format` values from `TYPE_KEYWORDS`, the
 * table `validateJsonField` enforces. The bundled schema completion can't: our per-type keywords
 * live in `if/then` branches it never evaluates.
 */

const unquote = (raw: string) => raw.replace(/^"/, "").replace(/"$/, "")

const propertyNameOf = (
  property: SyntaxNode,
  state: EditorState,
): string | undefined => {
  const name = property.getChild("PropertyName")

  return name ? unquote(state.sliceDoc(name.from, name.to)) : undefined
}

/** The keywords a field of `baseType` still accepts, minus the ones already written. */
export const availableKeywords = (
  baseType: JsonFieldType | undefined,
  present: ReadonlySet<string>,
): string[] => {
  const all = baseType
    ? [...COMMON_KEYWORDS, ...TYPE_KEYWORDS[baseType]]
    : [...COMMON_KEYWORDS]

  return all.filter((keyword) => !present.has(keyword))
}

const enclosingObject = (node: SyntaxNode): SyntaxNode | null => {
  for (
    let current: SyntaxNode | null = node;
    current;
    current = current.parent
  ) {
    if (current.name === "Object") {
      return current
    }
  }

  return null
}

const propertyKeys = (object: SyntaxNode, state: EditorState): string[] => {
  const keys: string[] = []
  for (
    let property = object.firstChild;
    property;
    property = property.nextSibling
  ) {
    if (property.name !== "Property") {
      continue
    }
    const name = property.getChild("PropertyName")
    if (name) {
      keys.push(unquote(state.sliceDoc(name.from, name.to)))
    }
  }

  return keys
}

const isBaseType = (value: string): value is JsonFieldType =>
  (Object.values(JsonFieldType) as string[]).includes(value)

/** Reads the object's declared `type`, tolerating the nullable `[T, "null"]` form. */
const declaredType = (
  object: SyntaxNode,
  state: EditorState,
): JsonFieldType | undefined => {
  for (
    let property = object.firstChild;
    property;
    property = property.nextSibling
  ) {
    if (property.name !== "Property") {
      continue
    }
    const name = property.getChild("PropertyName")
    if (!name || unquote(state.sliceDoc(name.from, name.to)) !== "type") {
      continue
    }

    // The value is the property's last child; a `:` token sits between it and the name.
    const value = property.lastChild
    if (!value || value === name) {
      return undefined
    }
    if (value.name === "String") {
      const literal = unquote(state.sliceDoc(value.from, value.to))

      return isBaseType(literal) ? literal : undefined
    }
    if (value.name === "Array") {
      for (let item = value.firstChild; item; item = item.nextSibling) {
        if (item.name !== "String") {
          continue
        }
        const literal = unquote(state.sliceDoc(item.from, item.to))
        if (isBaseType(literal)) {
          return literal
        }
      }
    }

    return undefined
  }

  return undefined
}

/**
 * Roles alternate down the tree: a field's `properties` is a map whose values are fields, and
 * `items` is a field. Classifying by ancestry, not the key name, lets a field an author named
 * `properties` still complete.
 */
type ObjectRole = "field" | "map" | "other"

const objectRole = (object: SyntaxNode, state: EditorState): ObjectRole => {
  const parent = object.parent
  if (!parent || parent.name === "JsonText") {
    return "field" // the root schema
  }
  if (parent.name !== "Property" || parent.parent?.name !== "Object") {
    return "other"
  }

  const container = objectRole(parent.parent, state)
  if (container === "map") {
    return "field" // any value in a properties map is a field definition
  }
  if (container === "field") {
    const key = propertyNameOf(parent, state)
    if (key === "properties") {
      return "map"
    }
    if (key === "items") {
      return "field"
    }
  }

  return "other"
}

/**
 * A `properties` record's keys are the author's own field names: completing keywords there would
 * create a field named `type`.
 */
const isPropertiesMap = (object: SyntaxNode, state: EditorState): boolean =>
  objectRole(object, state) === "map"

/**
 * Where an object property key goes: inside a key being typed, or after `{` or `,`. Null at value
 * positions, which belong to value completion.
 */
const propertyNameContext = (
  node: SyntaxNode,
  pos: number,
  state: EditorState,
): { object: SyntaxNode; from: number; to: number } | null => {
  const contextForObject = (
    object: SyntaxNode | null,
    from: number,
    to: number,
  ) => (object && !isPropertiesMap(object, state) ? { object, from, to } : null)

  if (node.name === "PropertyName" || node.name === "⚠") {
    return contextForObject(
      node.parent && enclosingObject(node.parent),
      node.from,
      node.to,
    )
  }

  if (node.name === "Object") {
    return contextForObject(node, pos, pos)
  }

  return null
}

/**
 * For a cursor inside a string value: its key, and whether it sits in an array (the nullable
 * `[T, "null"]` form).
 */
const valueContext = (
  node: SyntaxNode,
  state: EditorState,
): { key: string; inArray: boolean; from: number; to: number } | null => {
  if (node.name !== "String") {
    return null
  }
  const parent = node.parent
  if (parent?.name === "Property") {
    const key = propertyNameOf(parent, state)

    return key ? { key, inArray: false, from: node.from, to: node.to } : null
  }
  if (parent?.name === "Array" && parent.parent?.name === "Property") {
    const key = propertyNameOf(parent.parent, state)

    return key ? { key, inArray: true, from: node.from, to: node.to } : null
  }

  return null
}

/** The enum values offered for a `type` or `format` value; empty for any other key. */
const valueOptions = (key: string, inArray: boolean): string[] => {
  if (key === "type") {
    const types = Object.values(JsonFieldType)

    return inArray ? [...types, NULL] : types
  }
  if (key === "format") {
    return [...JSON_STRING_FORMATS]
  }

  return []
}

export const jsonFieldCompletion = (
  context: CompletionContext,
): CompletionResult | null => {
  const node = syntaxTree(context.state).resolveInner(context.pos, -1)

  const value = valueContext(node, context.state)
  if (value) {
    // `from` sits on the opening quote, so the text CodeMirror would match against labels
    // includes it; filter manually and disable the built-in filter, or every option is hidden.
    const typed = context.state.sliceDoc(value.from + 1, context.pos)
    const options = valueOptions(value.key, value.inArray)
      .filter((literal) => literal.startsWith(typed))
      .map((literal) => ({
        label: literal,
        type: "constant",
        apply: `"${literal}"`,
      }))

    return options.length
      ? { from: value.from, to: value.to, options, filter: false }
      : null
  }

  const place = propertyNameContext(node, context.pos, context.state)
  if (!place) {
    return null
  }

  // An open quoted key already being typed ("mi|) — replace from its opening quote.
  const quoted = context.matchBefore(/"[^"\\]*/)
  const word = context.matchBefore(/\w+/)
  if (!context.explicit && !quoted && !word) {
    return null
  }

  const from = quoted ? quoted.from : word ? word.from : place.from
  // Swallow a closing quote sitting under the cursor so we don't leave a stray one.
  const to =
    context.state.sliceDoc(context.pos, context.pos + 1) === '"'
      ? context.pos + 1
      : Math.max(context.pos, place.to)

  const typed = quoted
    ? context.state.sliceDoc(quoted.from + 1, context.pos)
    : (word?.text ?? "")

  const present = new Set(propertyKeys(place.object, context.state))
  present.delete(typed)

  // Filtered here, as above: `from` covers the opening quote.
  const options: Completion[] = availableKeywords(
    declaredType(place.object, context.state),
    present,
  )
    .filter((keyword) => keyword.startsWith(typed))
    .map((keyword) => ({
      label: keyword,
      type: "property",
      info: KEYWORD_DESCRIPTIONS[keyword],
      apply: `"${keyword}": `,
    }))

  return options.length ? { from, to, options, filter: false } : null
}
